import { gql } from '@apollo/client';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  AccountType,
  Mutation,
  PhoneGroup,
  Query,
  ResidentialProfileInfo,
  Role,
  SmbProfileInfo,
  UpdateAccountInfoInput,
  UpdateAccountInfoSmbInput,
} from '@mfe/shared/schema-types';

import { selectUserInfo } from '.';
import { setUser } from '../auth';
import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQuery,
  graphqlQueryWithErrors,
} from '../graphql';
import { getPendingInstallation, setPendingInstallation, setPendingInstallationError } from '../pendingInstallation';
import {
  refetchUserInfo as refetchUserInfoAction,
  setUpdateError,
  setUpdateSuccess,
  setUserInfo,
  updateContactInfo as updateContactInfoAction,
  updateContactInfoSMB as updateContactInfoSMBAction,
} from './userInfoSlice';
import { scrollToTop } from '../utils';
import { waitForToken } from '../utils/utilsSagas';
import { getUpdateErrorMessage } from './utils';

const UPDATE_CONTACT_INFO = gql`
  mutation updateContactInfo($input: UpdateAccountInfoInput!) {
    updateAccountInfo(input: $input) {
      email
      phone {
        primary {
          phoneNumber
          labels
        }
        mobile
        secondary {
          phoneNumber
          labels
        }
      }
      address {
        billing {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
        shipping {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
      }
    }
  }
`;

const UPDATE_CONTACT_INFO_SMB = gql`
  mutation updateContactInfoSMB($input: UpdateAccountInfoSMBInput!) {
    updateAccountInfoSMB(input: $input) {
      name
      email
      phone {
        primary {
          phoneNumber
          labels
        }
        mobile
        secondary {
          phoneNumber
          labels
        }
      }
      address {
        billing {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
        shipping {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
      }
    }
  }
`;

const GET_PROFILE_INFO = gql`
  query getProfileInfo($refetchData: Boolean) {
    getProfileInfo(refetchData: $refetchData) {
      accountType
      username
      partyId
      accountNumber
      isPreInstall
      address {
        billing {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
        shipping {
          addressLines
          countryCode
          municipality
          postalCode
          region
        }
      }
      ... on ResidentialProfileInfo {
        phone {
          mobile
          secondary {
            phoneNumber
            labels
          }
          primary {
            phoneNumber
            labels
          }
        }
        email
        fullName
        firstName
        lastName
      }

      ... on SMBProfileInfo {
        businessName
        accountHolder {
          partyId
          phone {
            mobile
            secondary {
              phoneNumber
              labels
            }
            primary {
              phoneNumber
              labels
            }
          }
          email
          fullName
          firstName
          lastName
        }
        siteContact {
          partyId
          phone {
            mobile
            secondary {
              phoneNumber
              labels
            }
            primary {
              phoneNumber
              labels
            }
          }
          email
          fullName
          firstName
          lastName
        }
        billingContact {
          partyId
          phone {
            mobile
            secondary {
              phoneNumber
              labels
            }
            primary {
              phoneNumber
              labels
            }
          }
          email
          fullName
          firstName
          lastName
        }
      }
    }
  }
`;

export function* updateContactInfoSMB(action: { type: string; payload: UpdateAccountInfoSmbInput }) {
  const { addresses, phones, deleteSecondaryPhone, email, role, shouldUpdateLoginUsername } = action.payload;

  const {
    userInfo: { username, accountHolder },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  const oldEmail = accountHolder.email;

  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: UPDATE_CONTACT_INFO_SMB,
    variables: {
      input: {
        phones,
        addresses,
        email,
        role,
        deleteSecondaryPhone,
        shouldUpdateLoginUsername,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError || errors || !data?.updateAccountInfoSMB) {
    const errorMessage = runtimeError
      ? getUpdateErrorMessage(runtimeError)
      : errors?.message ?? 'Error updating contact info';

    yield put(setUpdateError({ message: errorMessage }));
    return;
  }

  const { phone, address, email: updatedEmail } = data.updateAccountInfoSMB;

  yield put(
    setUserInfo({
      accountHolder:
        role === Role.AccountHolder
          ? {
              ...accountHolder,
              phone,
              email: updatedEmail,
            }
          : undefined,
      address: role === Role.Customer ? address : undefined,
      username: oldEmail === username ? updatedEmail : username,
    })
  );
  yield put(setUpdateSuccess());
  yield put(scrollToTop());
}

export function* updateContactInfo(action: { type: string; payload: UpdateAccountInfoInput }) {
  const { addresses, phones, deleteSecondaryPhone, email, shouldUpdateLoginUsername } = action.payload;

  const {
    userInfo: { email: oldEmail, username },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: UPDATE_CONTACT_INFO,
    variables: {
      input: {
        phones,
        addresses,
        email,
        deleteSecondaryPhone,
        shouldUpdateLoginUsername,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError || errors || !data?.updateAccountInfo) {
    const errorMessage = runtimeError
      ? getUpdateErrorMessage(runtimeError)
      : errors?.message ?? 'Error updating contact info';

    yield put(setUpdateError({ message: errorMessage }));
    return;
  }

  const { phone, address, email: updatedEmail } = data.updateAccountInfo;

  yield put(
    setUserInfo({
      phone,
      address,
      email: updatedEmail,
      username: oldEmail === username ? updatedEmail : username,
    })
  );
  yield put(setUpdateSuccess());
}

export function* fetchUserInfo(payload?: { refetchData: boolean }) {
  const data: Query = yield call(graphqlQuery, {
    query: GET_PROFILE_INFO,
    variables: {
      refetchData: payload?.refetchData ?? true,
    },
    fetchPolicy: payload?.refetchData ? 'no-cache' : 'cache-first',
  });

  const getProfileInfo = data?.getProfileInfo;
  const isSMBAccount = getProfileInfo?.accountType === AccountType.Smb;

  if (isSMBAccount) {
    const result = getProfileInfo as SmbProfileInfo;
    yield put(
      setUserInfo({
        username: result?.username ?? undefined,
        partyId: result?.partyId ?? undefined,
        accountNumber: result?.accountNumber ?? undefined,
        accountHolder: {
          partyId: result?.accountHolder?.partyId ?? '',
          fullName: result?.accountHolder?.fullName ?? '',
          firstName: result?.accountHolder?.firstName ?? '',
          lastName: result?.accountHolder?.lastName ?? '',
          email: result?.accountHolder?.email ?? '',
          phone: result?.accountHolder?.phone ?? ({} as PhoneGroup),
        },
        address: result?.address ?? undefined,
        isPreInstall: result?.isPreInstall ?? undefined,
        accountType: result?.accountType ?? undefined,
        businessName: result?.businessName ?? undefined,
      })
    );
    return;
  }

  const result = getProfileInfo as ResidentialProfileInfo;
  yield put(
    setUserInfo({
      username: result?.username ?? undefined,
      partyId: result?.partyId ?? undefined,
      accountNumber: result?.accountNumber ?? undefined,
      fullName: result?.fullName ?? undefined,
      firstName: result?.firstName ?? undefined,
      lastName: result?.lastName ?? undefined,
      email: result?.email ?? undefined,
      phone: result?.phone ?? undefined,
      address: result?.address ?? undefined,
      isPreInstall: result?.isPreInstall ?? undefined,
      accountType: result?.accountType ?? undefined,
    })
  );
}

export function* refetchUserInfo() {
  yield call(fetchUserInfo, { refetchData: true });
}

const GET_PENDING_INSTALLATION_DETAILS = gql`
  query getPendingInstallationDetails($refetchData: Boolean) {
    getPendingInstallationDetails(refetchData: $refetchData) {
      isEquipmentShipped
      hasPendingInstallation
      newOfferName
      externalWorkOrderId
      schedule {
        from
        to
      }
    }
  }
`;

function* fetchPendingInstallationDetails() {
  yield call(waitForToken);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_PENDING_INSTALLATION_DETAILS,
    variables: { refetchData: true },
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError) {
    yield put(setPendingInstallationError(runtimeError));
  } else if (errors) {
    yield put(setPendingInstallationError(errors));
  } else if (data) {
    yield put(setPendingInstallation(data.getPendingInstallationDetails));
  }
}

export function* watchUserInfo() {
  yield all([
    takeLatest(setUser.type, fetchUserInfo),
    takeLatest(getPendingInstallation.type, fetchPendingInstallationDetails),
    takeLatest(refetchUserInfoAction.type, refetchUserInfo),
    takeLatest(updateContactInfoAction.type, updateContactInfo),
    takeLatest(updateContactInfoSMBAction.type, updateContactInfoSMB),
  ]);
}
